import Inventory from '../inventory'

export enum Mode {
  walking = 'walking',
  sprinting = 'sprinting',
  flying = 'flying',
  sprintFlying = 'sprintFlying',
  sneaking = 'sneaking'
}

export enum GameMode {
  survival = 'survival',
  creative = 'creative'
}

export enum Speed {
  // walking = 4.317,
  walking = 5.612,
  sprinting = 8.0,
  // flying = 10.89,
  flying = 21.78,
  sprintFlying = 32.0,
  // sneaking = 1.95
  sneaking = 2.55
}

export type MoraleTier = 'steady' | 'uneasy' | 'despairing' | 'critical'

export default class Player {
  mode = Mode.walking
  speed = Speed[this.mode]
  gameMode = GameMode.survival

  // inventory
  inventory = new Inventory()

  // survival stats
  health = 20 // 20 = 10 hearts
  maxHealth = 20
  hunger = 20 // 20 = 10 drumsticks
  maxHunger = 20
  morale = 100
  maxMorale = 100
  alive = true

  // fluid stats
  air = 15 // seconds of breath while submerged
  maxAir = 15
  onFire = 0 // seconds of burning left
  fluidSlow = 1 // horizontal speed multiplier while wading in fluid

  // fall damage tracking
  fallStartY = 0
  isFalling = false

  // hunger depletion timer
  hungerTimer = 0
  regenTimer = 0
  starveTimer = 0

  // spawn protection - no damage for first few seconds
  spawnProtection = 0

  clamp = (value: number, min: number, max: number) => {
    return Math.max(min, Math.min(max, value))
  }

  updateSpeed = () => {
    this.speed =
      Speed[this.mode] * this.getMoraleSpeedMultiplier() * this.fluidSlow
  }

  setFluidSlow(factor: number) {
    if (factor === this.fluidSlow) return
    this.fluidSlow = factor
    this.updateSpeed()
  }

  setOnFire(seconds: number) {
    if (this.gameMode !== GameMode.survival || !this.alive) return
    this.onFire = Math.max(this.onFire, seconds)
  }

  extinguish() {
    this.onFire = 0
  }

  setMode(Mode: Mode) {
    this.mode = Mode
    this.updateSpeed()
  }

  setGameMode(gameMode: GameMode) {
    this.gameMode = gameMode
    this.air = this.maxAir
    this.onFire = 0
    this.fluidSlow = 1
    if (gameMode === GameMode.survival) {
      this.health = this.maxHealth
      this.hunger = this.maxHunger
      this.morale = this.maxMorale
      this.alive = true
      this.mode = Mode.walking
      this.updateSpeed()
      this.spawnProtection = 5
      this.inventory.clear()
    } else {
      this.alive = true
      this.isFalling = false
      this.spawnProtection = 0
      this.mode = Mode.flying
      this.updateSpeed()
    }
  }

  takeDamage(amount: number) {
    if (this.gameMode !== GameMode.survival || !this.alive) return false
    if (this.spawnProtection > 0) return false
    this.health = Math.max(0, this.health - amount)
    if (this.health <= 0) {
      this.alive = false
    }
    return true
  }

  heal(amount: number) {
    if (this.gameMode !== GameMode.survival || !this.alive) return
    this.health = Math.min(this.maxHealth, this.health + amount)
  }

  depleteHunger(amount: number) {
    if (this.gameMode !== GameMode.survival || !this.alive) return
    this.hunger = Math.max(0, this.hunger - amount)
  }

  adjustMorale(amount: number) {
    if (this.gameMode !== GameMode.survival || !this.alive) return
    const nextMorale = this.clamp(this.morale + amount, 0, this.maxMorale)
    if (nextMorale === this.morale) return
    this.morale = nextMorale
    this.updateSpeed()
  }

  restoreMorale(amount: number) {
    if (amount <= 0) return
    this.adjustMorale(amount)
  }

  setMorale(amount: number) {
    this.morale = this.clamp(amount, 0, this.maxMorale)
    this.updateSpeed()
  }

  getMoraleTier(): MoraleTier {
    if (this.morale >= 70) return 'steady'
    if (this.morale >= 40) return 'uneasy'
    if (this.morale >= 15) return 'despairing'
    return 'critical'
  }

  getMoraleLabel() {
    switch (this.getMoraleTier()) {
      case 'steady':
        return 'Steady'
      case 'uneasy':
        return 'Uneasy'
      case 'despairing':
        return 'Despairing'
      case 'critical':
        return 'Critical'
      default:
        return 'Steady'
    }
  }

  getMoraleSpeedMultiplier() {
    if (this.gameMode !== GameMode.survival) return 1
    switch (this.getMoraleTier()) {
      case 'uneasy':
        return 0.95
      case 'despairing':
        return 0.85
      case 'critical':
        return 0.75
      default:
        return 1
    }
  }

  getMoraleMiningMultiplier() {
    if (this.gameMode !== GameMode.survival) return 1
    switch (this.getMoraleTier()) {
      case 'uneasy':
        return 1.15
      case 'despairing':
        return 1.35
      case 'critical':
        return 1.6
      default:
        return 1
    }
  }

  getMoraleHungerInterval(baseSeconds: number) {
    switch (this.getMoraleTier()) {
      case 'uneasy':
        return 25
      case 'despairing':
        return 20
      case 'critical':
        return 15
      default:
        return baseSeconds
    }
  }

  eat(amount: number) {
    if (this.gameMode !== GameMode.survival || !this.alive) return false
    if (this.hunger >= this.maxHunger || amount <= 0) return false
    this.hunger = Math.min(this.maxHunger, this.hunger + amount)
    return true
  }

  respawn() {
    this.health = this.maxHealth
    this.hunger = this.maxHunger
    this.morale = this.maxMorale
    this.alive = true
    this.isFalling = false
    this.air = this.maxAir
    this.onFire = 0
    this.fluidSlow = 1
    this.mode = this.gameMode === GameMode.creative ? Mode.flying : Mode.walking
    this.updateSpeed()
    this.spawnProtection = 5 // 5 seconds of invulnerability after spawn
    // inventory is kept on respawn
  }

  falling = 38.4

  jump = 1.2522

  body = {
    height: 1.8,
    width: 0.5
  }
}
